import memoize from 'lodash/memoize';
import type { GraphQLError } from 'graphql';
import jwtDecode from 'jwt-decode';
import { graphql } from 'react-relay';

import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { getStatusCode } from '@confluence/error-boundary/entry-points/isStatusCodeError.ts';
import { fg } from '@confluence/feature-gating';
import type { QueryInteropSupportedResult } from '@confluence/apollo-relay-interop/entry-points/query.ts';
import { queryInterop } from '@confluence/apollo-relay-interop/entry-points/query.ts';
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';

import type {
	ExternalShareMediaDownloadTokenQuery as ExternalShareMediaDownloadTokenQueryT,
	ExternalShareMediaDownloadTokenQueryVariables,
} from './__types__/ExternalShareMediaDownloadTokenQuery.ts';
import type {
	MediaUploadTokenQuery as MediaUploadTokenQueryT,
	MediaUploadTokenQueryCommentQuery as CommentMediaUploadTokenQueryT,
	MediaUploadTokenQueryVariables,
	MediaUploadTokenQueryCommentQueryVariables,
} from './__types__/MediaUploadTokenQuery.ts';
import type {
	ExternalContentMediaSessionQuery as ExternalContentMediaSessionQueryT,
	ExternalContentMediaSessionQueryVariables,
} from './__types__/ExternalContentMediaSessionQuery.ts';
import type {
	graphqlHelpersDSPIsActionEnabledForContentQuery$data,
	graphqlHelpersDSPIsActionEnabledForContentQuery$variables,
} from './__generated__/graphqlHelpersDSPIsActionEnabledForContentQuery.graphql.ts';
import type {
	graphqlHelpersFetchMediaTokenQuery$data,
	graphqlHelpersFetchMediaTokenQuery$variables,
} from './__generated__/graphqlHelpersFetchMediaTokenQuery.graphql.ts';
import ExternalShareMediaDownloadTokenQuery from './__generated__/ExternalShareMediaDownloadTokenQuery.graphql.ts';
import MediaUploadTokenQuery from './__generated__/MediaUploadTokenQuery.graphql.ts';
import CommentMediaUploadTokenQuery from './__generated__/MediaUploadTokenQueryCommentQuery.graphql.ts';
import { experimentalFileUploadMutate } from './experimentalFileUploadMutate.ts';
import ExternalContentMediaSessionQuery from './__generated__/ExternalContentMediaSessionQuery.graphql.ts';
import type {
	graphqlHelpersFetchJiraMediaTokenQuery$data,
	graphqlHelpersFetchJiraMediaTokenQuery$variables,
} from './__generated__/graphqlHelpersFetchJiraMediaTokenQuery.graphql.ts';
const DSPIsActionEnabledForContentQuery = graphql`
	query graphqlHelpersDSPIsActionEnabledForContentQuery(
		$action: DataSecurityPolicyAction!
		$contentId: ID!
		$contentStatus: DataSecurityPolicyDecidableContentStatus!
		$contentVersion: Int!
		$spaceKey: String
	) {
		dataSecurityPolicy {
			isActionEnabledForContent(
				action: $action
				contentId: $contentId
				contentStatus: $contentStatus
				contentVersion: $contentVersion
				spaceKey: $spaceKey
			) {
				action
				allowed
			}
		}
	}
`;

const FetchMediaTokenQuery = graphql`
	query graphqlHelpersFetchMediaTokenQuery(
		$contentId: ID
		$status: [String]
		$missingContentId: Boolean = false
	) {
		content(id: $contentId, status: $status) @skip(if: $missingContentId) {
			nodes {
				id
				body {
					atlas_doc_format {
						mediaToken {
							token
						}
					}
				}
			}
		}
		mediaConfiguration {
			clientId
			fileStoreUrl
		}
	}
`;

export const FetchMediaTokenWithMediaItemsQuery = graphql`
	query graphqlHelpersFetchMediaTokenWithMediaItemsQuery(
		$cloudId: ID!
		$contentId: ID
		$status: [String]
		$missingContentId: Boolean = false
	) {
		content(id: $contentId, status: $status) @skip(if: $missingContentId) {
			nodes {
				id
				body {
					atlas_doc_format {
						mediaToken {
							token
						}
						mediaItems(cloudId: $cloudId) {
							id
							type
							details {
								name
								size
								mimeType
								mediaType
								processingStatus
								failReason
								createdAt
								preview {
									cdnUrl
								}
								artifactsList {
									createdAt
									mimeType
									name
									processingStatus
									size
									url
								}
								representations {
									image {
										_empty
									}
								}
								mediaMetadata {
									duration
								}
								abuseClassification {
									classification
									confidence
								}
							}
						}
					}
				}
			}
		}
		mediaConfiguration {
			clientId
			fileStoreUrl
		}
	}
`;

const JiraFetchMediaTokenQuery = graphql`
	query graphqlHelpersFetchJiraMediaTokenQuery($issueAri: ID!, $maxTokenLength: Int = 65536) {
		jira {
			issueById(id: $issueAri) {
				mediaReadToken(maxTokenLength: $maxTokenLength) @optIn(to: "JiraMediaReadTokenInIssue") {
					clientId
					endpointUrl
					tokenLifespanInSeconds
					tokensWithFiles {
						edges {
							node {
								token
							}
						}
					}
				}
			}
		}
	}
`;

type setAttachmentRequestBody = {
	size: number;
	contentType: string;
	mimeType: string;
	fileName: string;
	pageId: string;
	fileStoreId: string;
	minorEdit: boolean;
	renderEditorHTML: boolean;
	collectionName: string;
};

let isFirstLoad = true;

export const setAttachment = (requestBody: setAttachmentRequestBody): void | Promise<any> => {
	if (requestBody.contentType === 'comment') {
		// Hard disable drag-and-drop upload functionality for comments
		return;
	}

	// Normal uploads (file browser, copy-paste, etc.) go through the regular upload mutation.
	return experimentalFileUploadMutate({ variables: requestBody });
};

export const fetchMediaToken = (
	contentId: string,
): Promise<QueryInteropSupportedResult<graphqlHelpersFetchMediaTokenQuery$data>> =>
	queryInterop<
		graphqlHelpersFetchMediaTokenQuery$data,
		graphqlHelpersFetchMediaTokenQuery$variables
	>({
		query: FetchMediaTokenQuery,
		variables: {
			contentId,
			status: ['current', 'draft', 'archived'],
			missingContentId: !contentId,
		},
		context: {
			allowOnExternalPage: true,
		},
		// If SSR has populated the cache then we want to use the cache when rendering SPA-over-SSR
		fetchPolicy: isFirstLoad ? 'cache-first' : 'network-only',
	}).then(
		/* onFulfilled */ (value) => {
			isFirstLoad = false;

			try {
				throttleFetchMediaTokenErrors(contentId)(value?.error?.graphQLErrors);
			} catch {}

			return value;
		},
		/* onRejected */ (reason: ApolloErrorInterop) => {
			try {
				throttleFetchMediaTokenErrors(contentId)(reason?.graphQLErrors, reason?.networkError);
			} catch {}

			throw reason;
		},
	);

let isFirstLoadJira = true;

export const fetchJiraMediaToken = (
	issueAri: string,
): Promise<QueryInteropSupportedResult<graphqlHelpersFetchJiraMediaTokenQuery$data>> =>
	queryInterop<
		graphqlHelpersFetchJiraMediaTokenQuery$data,
		graphqlHelpersFetchJiraMediaTokenQuery$variables
	>({
		query: JiraFetchMediaTokenQuery,
		variables: { issueAri, maxTokenLength: 65536 },
		context: {
			allowOnExternalPage: true,
		},
		// If SSR has populated the cache then we want to use the cache when rendering SPA-over-SSR
		fetchPolicy: isFirstLoadJira ? 'cache-first' : 'network-only',
	}).then(
		/* onFulfilled */ (value) => {
			isFirstLoadJira = false;

			try {
				throttleFetchMediaTokenErrors(issueAri)(value?.error?.graphQLErrors);
			} catch {}

			return value;
		},
		/* onRejected */ (reason: ApolloErrorInterop) => {
			try {
				throttleFetchMediaTokenErrors(issueAri)(reason?.graphQLErrors, reason?.networkError);
			} catch {}

			throw reason;
		},
	);

/**
 * `fetchMediaToken` is called repetitively to renew the token before it
 * expires. If an error condition arises (because of frontend or backend), the
 * same error(s) may be reported as unhandled to our analytics and monitoring at
 * a very high rate/volume. Such repetitive reporting fron one user in one tab
 * is noisy. `throttleFetchMediaTokenErrors` attempts to reduce the noise.
 */
const throttleFetchMediaTokenErrors = memoize((_cacheKey: string) => {
	/**
	 * The errors that happened last time. The `GraphQLError` instances are big
	 * and are not trivial to compare so `prevErrors` caches partial info.
	 */
	let prevErrors: Record<string, string> | undefined = undefined;

	return (
		graphQLErrors?: ApolloErrorInterop['graphQLErrors'],
		networkError?: ApolloErrorInterop['networkError'],
	) => {
		// Each of `graphQLErrors` and `networkError` are reported to analytics and
		// monitoring so type of `Error` does not matter to what follows and
		// `GraphQLError` is the most convenient TypeScript type to deal with.
		if (networkError) {
			graphQLErrors = (graphQLErrors || []).concat(networkError as GraphQLError);
		}

		if (graphQLErrors) {
			/**
			 * The new/current errors which will become `prevErrors` at the end of
			 * this iteration.
			 */
			const nextErrors = {};

			for (const graphQLError of graphQLErrors) {
				// Extract the partial, easy-to-compare, cachable info from the big
				// `GraphQLError` instance:
				const message =
					graphQLError.message ||
					String(graphQLError) ||
					graphQLError.originalError?.message ||
					String(graphQLError.originalError);
				const { path } = graphQLError;
				const key = (Array.isArray(path) && path.join('.')) || message;
				if (key) {
					const value = getStatusCode(graphQLError) || message;
					if (value) {
						nextErrors[key] = value;
						// If a similar error happened in the previous iteration, do not
						// report it to analytics and monitoring again:
						if (prevErrors?.[key] === value) {
							markErrorAsHandled(graphQLError);
						}
					}
				}
			}

			prevErrors = nextErrors;
		} else {
			// If `MediaDownloadTokenQuery` succeeds, report subsequent/future errors
			// even if similar ones occurred and were reported before the current
			// success - there is a meaningful change in state and it may be useful in
			// debugging.
			prevErrors = undefined;
		}
	};
});

type ExternalMediaTokenQuery = ExternalShareMediaDownloadTokenQueryT;
type ExternalMediaTokenQueryVariables = ExternalShareMediaDownloadTokenQueryVariables;

export const fetchExternalShareMediaToken = (
	contentId: string,
): Promise<QueryInteropSupportedResult<ExternalShareMediaDownloadTokenQueryT>> =>
	queryInterop<ExternalMediaTokenQuery, ExternalMediaTokenQueryVariables>({
		query: ExternalShareMediaDownloadTokenQuery,
		variables: {
			contentId,
		},
		context: {
			allowOnExternalPage: true,
		},
		fetchPolicy: 'network-only',
	});

export const fetchExternalContentMediaSessionToken = (
	shareToken: string,
): Promise<QueryInteropSupportedResult<ExternalContentMediaSessionQueryT>> =>
	queryInterop<ExternalContentMediaSessionQueryT, ExternalContentMediaSessionQueryVariables>({
		query: ExternalContentMediaSessionQuery,
		variables: {
			shareToken,
		},
		context: {
			allowOnExternalPage: true,
		},
		fetchPolicy: 'network-only',
	});

export const getUploadTokenV0 = (
	contentId: string,
): Promise<QueryInteropSupportedResult<MediaUploadTokenQueryT>> =>
	queryInterop<MediaUploadTokenQueryT, MediaUploadTokenQueryVariables>({
		query: MediaUploadTokenQuery,
		variables: {
			contentId,
		},
		fetchPolicy: 'no-cache',
	});

type JwtPayload = {
	exp: number; // Expiration time in seconds since epoch
};

export const getUploadToken = async (
	contentId: string,
): Promise<QueryInteropSupportedResult<MediaUploadTokenQueryT>> => {
	if (!fg('confluence-media-upload-token-cache')) {
		return getUploadTokenV0(contentId);
	}

	try {
		const { data } = await queryInterop<MediaUploadTokenQueryT, MediaUploadTokenQueryVariables>({
			query: MediaUploadTokenQuery,
			variables: { contentId },
			fetchPolicy: 'cache-first',
		});
		const token = data?.contentMediaSession?.token?.value;
		if (token) {
			const { exp } = jwtDecode<JwtPayload>(token);
			const currentTime = Math.floor(Date.now() / 1000);
			// Check if the token has not expired
			if (exp > currentTime) {
				return { data };
			}
		}
	} catch {
		// Fall through to refetch on decode failure
	}
	// Refetch with network-only policy if token is expired, or invalid
	return queryInterop<MediaUploadTokenQueryT, MediaUploadTokenQueryVariables>({
		query: MediaUploadTokenQuery,
		variables: { contentId },
		fetchPolicy: 'network-only',
	});
};

export const getCommentUploadToken = async (
	contentId: string,
	cloudId: string,
): Promise<QueryInteropSupportedResult<CommentMediaUploadTokenQueryT>> => {
	try {
		const { data } = await queryInterop<
			CommentMediaUploadTokenQueryT,
			MediaUploadTokenQueryCommentQueryVariables
		>({
			query: CommentMediaUploadTokenQuery,
			variables: { cloudId, contentId },
			fetchPolicy: 'cache-first',
		});
		const token = data?.contentMediaSession?.token?.value;
		if (token) {
			const { exp } = jwtDecode<JwtPayload>(token);
			const currentTime = Math.floor(Date.now() / 1000);
			// Check if the token has not expired
			if (exp > currentTime) {
				return { data };
			}
		}
	} catch {
		// Fall through to refetch on decode failure
	}
	// Refetch with network-only policy if token is expired, or invalid
	return queryInterop<CommentMediaUploadTokenQueryT, MediaUploadTokenQueryCommentQueryVariables>({
		query: CommentMediaUploadTokenQuery,
		variables: { cloudId, contentId },
		fetchPolicy: 'network-only',
	});
};

export const getIsExportEnabledForContent = (
	contentId: string,
	spaceKey?: string,
): Promise<QueryInteropSupportedResult<graphqlHelpersDSPIsActionEnabledForContentQuery$data>> => {
	const action = fg('cc-dlp_split-attachment-dl-from-export-dsp')
		? 'ATTACHMENT_DOWNLOAD'
		: 'PAGE_EXPORT';

	const variables: graphqlHelpersDSPIsActionEnabledForContentQuery$variables = {
		action,
		contentId,
		contentStatus: 'CURRENT',
		contentVersion: 1,
		...(spaceKey && { spaceKey }),
	};
	/* Note -> a relay-compatible replacement is available in the @confluence/data-security package.
	 * Using queryInterop for compatibility with Relay migration
	 */
	return queryInterop<
		graphqlHelpersDSPIsActionEnabledForContentQuery$data,
		graphqlHelpersDSPIsActionEnabledForContentQuery$variables
	>({
		query: DSPIsActionEnabledForContentQuery,
		variables,
		context: {
			allowOnExternalPage: true,
		},
		fetchPolicy: 'cache-first',
	});
};
